import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import {
  buildReportsRegistry,
  type ReportDefinition,
  type SelectOptionItem,
} from '@/reports/registry';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage, httpClient } from '@/utils/httpClient';
import { entityService } from '@/services/EntityService';
import { shelfMapService } from '@/services/shelfMapService';
import { DYNAMIC_OPTIONS } from '@/reports/constants';
import {
  OUTPUT_FORMAT_KEY,
  PRE_OPENED_WINDOW_KEY,
  type ReportOutputFormat,
} from '@/utils/reportOutput';
import { preOpenWindow } from '@/utils/pdfHelpers';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { filterLeafLocationOptions } from '@/utils/locationHierarchy';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';
import { useCatalogLocationLabels } from '@/hooks/useCatalogLocationLabels';
import { useAuth } from '@/hooks/useAuth';

/** Shape of navigation state that can be passed to pre-select a report with form values */
export interface ReportsPageNavState {
  reportId?: string;
  formValues?: Record<string, unknown>;
}

export interface PersonOption {
  id: string;
  displayName: string;
  entityIdentifier: string | null;
}

type ReportFormValues = Record<string, unknown>;

/** Stable empty array to avoid re-render loops in controlled MUI Autocomplete multiple */
const EMPTY_PERSONS: PersonOption[] = [];

/** Reports visible to the comptroller and hr roles — finance/SOC-scoped only. */
const COMPTROLLER_REPORT_IDS = new Set<string>(['assets-currently-assigned']);

/** Params hidden from comptroller/hr on the assets-currently-assigned report. */
const COMPTROLLER_HIDDEN_PARAMS = new Set<string>(['excludePnp', 'excludeSoc']);

/** Report IDs visible to the HR role — same restriction as comptroller. */
const HR_REPORT_IDS = COMPTROLLER_REPORT_IDS;

/** Params hidden from HR on the assets-currently-assigned report. */
const HR_HIDDEN_PARAMS = COMPTROLLER_HIDDEN_PARAMS;

const ENABLED_REPORT_IDS = new Set<string>([
  'customer-survey',
  'allocation-summary',
  'transaction-completion',
  'asset-acquisition-order-form',
  'outstanding-pos',
  'cost-of-business',
  'inventory-trends',
  'inventory-awareness',
  'dx-report',
  'cleared-service-members',
  'suggest-on-hand-inventory',
  'change-document',
  'all-ie',
  'assets-currently-assigned',
  'assets-approaching-expiration',
  'expired-assets',
  'ie-items-not-on-sm-phr',
  'overdue-assets',
  'primary-restock',
  'warehouse-shelf-map',
  'total-clothing-size-by-workforce-category',
  'property-book-listing',
  'inventory-movement-report',
]);

const resolveInitialValues = (report: ReportDefinition): ReportFormValues =>
  report.parameters.reduce<ReportFormValues>((acc, param) => {
    if (
      param.type !== 'person' &&
      param.type !== 'multiperson' &&
      param.defaultValue !== undefined
    ) {
      acc[param.name] = param.defaultValue;
    }
    return acc;
  }, {});

const sortedReportsDefault = buildReportsRegistry();

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s-]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

export const ReportsPage: React.FC = () => {
  const location = useLocation();
  const navState = (location.state ?? null) as ReportsPageNavState | null;
  const person = usePersonLabels('reports');
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('reports');
  const { excludeReportLabel, programLabel } = useRestrictedProgramLabels();
  const catalogLocationLabels = useCatalogLocationLabels();
  const { isComptroller, isHr, user } = useAuth();
  const isRestrictedRole = isComptroller || isHr;
  // Warehouse Shelf Map is only accessible when raw roles are exactly user or user+admin (#330)
  const rawRoles = user?.roles ?? [];
  const canAccessShelfMap =
    rawRoles.includes('user') && rawRoles.every((r) => r === 'user' || r === 'admin');

  const sortedReports = useMemo(
    () =>
      [
        ...buildReportsRegistry(
          person,
          ieKitLabel,
          ieKitLabelPlural,
          catalogLocationLabels.primaryLocationLabel,
          catalogLocationLabels.secondaryLocationLabel,
          excludeReportLabel,
        ),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [
      person,
      ieKitLabel,
      ieKitLabelPlural,
      catalogLocationLabels.primaryLocationLabel,
      catalogLocationLabels.secondaryLocationLabel,
      excludeReportLabel,
    ],
  );

  const [selectedReportId, setSelectedReportId] = useState<string | null>(() => {
    if (isRestrictedRole) return 'assets-currently-assigned';
    return navState?.reportId ?? sortedReportsDefault[0]?.id ?? null;
  });
  const [search, setSearch] = useState('');
  const [outputFormat, setOutputFormat] = useState<ReportOutputFormat>('html');
  const notifications = useNotifications();

  // Role-gated list (no search) — used for both the selected-report lookup and as the
  // base for the search-filtered display list, so a disallowed role cannot access a
  // hidden report by navigating to it directly via id (#330).
  const roleGatedReports = useMemo(() => {
    if (isRestrictedRole) {
      return sortedReports.filter((r) => (isHr ? HR_REPORT_IDS : COMPTROLLER_REPORT_IDS).has(r.id));
    }
    return sortedReports.filter((r) => r.id !== 'warehouse-shelf-map' || canAccessShelfMap);
  }, [sortedReports, isRestrictedRole, isHr, canAccessShelfMap]);

  const selectedReport = useMemo<ReportDefinition | undefined>(
    () => roleGatedReports.find((report) => report.id === selectedReportId),
    [selectedReportId, roleGatedReports],
  );

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return roleGatedReports;
    return roleGatedReports.filter((report) =>
      multiWordMatch(`${report.name} ${report.description}`, query),
    );
  }, [search, roleGatedReports]);

  const [formValues, setFormValues] = useState<ReportFormValues>(() => {
    const defaults = selectedReport ? resolveInitialValues(selectedReport) : {};
    return navState?.formValues ? { ...defaults, ...navState.formValues } : defaults;
  });

  // Person picker state
  const [personSearchInputs, setPersonSearchInputs] = useState<Record<string, string>>({});
  const [personOptions, setPersonOptions] = useState<Record<string, PersonOption[]>>({});
  const [personLoading, setPersonLoading] = useState<Record<string, boolean>>({});

  // Dynamic multiselect options state
  const [multiselectOptions, setMultiselectOptions] = useState<Record<string, string[]>>({});
  const [multiselectLoading, setMultiselectLoading] = useState<Record<string, boolean>>({});
  const [multiselectErrors, setMultiselectErrors] = useState<Record<string, string | null>>({});
  // Maps "param.name" → { locationName → locationEntityId } for shelf-map-locations (#370)
  const locationNameToIdRef = useRef<Record<string, Record<string, string>>>({});
  // Maps Parent NSN option label → catalog entity id for total-clothing-size-by-workforce-category
  const parentNsnLabelToIdRef = useRef<Record<string, string>>({});
  // Tracks the previously seen workforceCategory value so the scoped effect below can tell
  // whether it re-ran because of a workforceCategory change or a section change.
  const previousWorkforceCategoryRef = useRef<string | undefined>(undefined);

  // Dynamic select options state (single-select with API-loaded options)
  const [selectOptions, setSelectOptions] = useState<Record<string, SelectOptionItem[]>>({});
  const [selectLoading, setSelectLoading] = useState<Record<string, boolean>>({});

  // Person search effect - uses a ref to track active searches
  const personSearchTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const personSearchControllers = useRef<Record<string, AbortController>>({});

  // For comptroller and HR, pre-load all persons when the assets-currently-assigned report is selected
  useEffect(() => {
    if (!isRestrictedRole || selectedReportId !== 'assets-currently-assigned') return;

    let cancelled = false;
    setPersonLoading((prev) => ({ ...prev, persons: true }));

    entityService
      .listAll({
        type: 'person',
        order: 'display_name.asc',
        filters: { select: 'id,display_name,entity_identifier', status: 'eq.active' },
      })
      .then((results) => {
        if (cancelled) return;
        setPersonOptions((prev) => ({
          ...prev,
          persons: results.map((e) => ({
            id: e.id,
            displayName: e.display_name ?? 'Unknown',
            entityIdentifier: e.entity_identifier ?? null,
          })),
        }));
      })
      .catch(() => {
        // Ignore
      })
      .finally(() => {
        if (!cancelled) setPersonLoading((prev) => ({ ...prev, persons: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [isComptroller, selectedReportId]);

  const handlePersonSearch = useCallback((paramName: string, searchInput: string) => {
    // Clear previous timeout/controller for this param
    if (personSearchTimeouts.current[paramName]) {
      clearTimeout(personSearchTimeouts.current[paramName]);
    }
    if (personSearchControllers.current[paramName]) {
      personSearchControllers.current[paramName].abort();
    }

    setPersonSearchInputs((prev) => ({ ...prev, [paramName]: searchInput }));

    if (!searchInput.trim() || searchInput.length < 2) {
      setPersonOptions((prev) => ({ ...prev, [paramName]: [] }));
      setPersonLoading((prev) => ({ ...prev, [paramName]: false }));
      return;
    }

    setPersonLoading((prev) => ({ ...prev, [paramName]: true }));

    const controller = new AbortController();
    personSearchControllers.current[paramName] = controller;

    personSearchTimeouts.current[paramName] = setTimeout(async () => {
      try {
        const results = await entityService.searchEntities(searchInput, 'person');
        if (!controller.signal.aborted) {
          setPersonOptions((prev) => ({
            ...prev,
            [paramName]: results.map((e) => ({
              id: e.id,
              displayName: e.display_name ?? 'Unknown',
              entityIdentifier: e.entity_identifier ?? null,
            })),
          }));
        }
      } catch {
        // Ignore errors
      } finally {
        if (!controller.signal.aborted) {
          setPersonLoading((prev) => ({ ...prev, [paramName]: false }));
        }
      }
    }, 300);
  }, []);

  const handleSelectReport = (report: ReportDefinition) => {
    setSelectedReportId(report.id);
    setFormValues(resolveInitialValues(report));
  };

  // Scroll the pre-selected report into view when arriving via navigation state
  const selectedItemRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (navState?.reportId && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load dynamic multiselect options when report changes
  useEffect(() => {
    if (!selectedReport) return;

    const abortController = new AbortController();
    let mounted = true;

    const loadDynamicOptions = async () => {
      for (const param of selectedReport.parameters) {
        if (param.type === 'select' && param.dynamicOptions === DYNAMIC_OPTIONS.SHELF_MAP_LAYOUTS) {
          setSelectLoading((prev) => ({ ...prev, [param.name]: true }));
          try {
            const layouts = await shelfMapService.listLayouts();
            if (!mounted || abortController.signal.aborted) return;
            const options: SelectOptionItem[] = layouts.map((l) => ({
              value: l.id,
              label: l.warehouse_name ? `${l.name} (${l.warehouse_name})` : l.name,
            }));
            setSelectOptions((prev) => ({ ...prev, [param.name]: options }));
          } catch (error) {
            if (!mounted || abortController.signal.aborted) return;
            notifications.error('Failed to load shelf map layouts', getErrorMessage(error));
            setSelectOptions((prev) => ({ ...prev, [param.name]: [] }));
          } finally {
            if (mounted && !abortController.signal.aborted) {
              setSelectLoading((prev) => ({ ...prev, [param.name]: false }));
            }
          }
        }

        if (
          param.type === 'select' &&
          param.dynamicOptions === DYNAMIC_OPTIONS.WORKFORCE_CATEGORIES
        ) {
          setSelectLoading((prev) => ({ ...prev, [param.name]: true }));
          try {
            const persons = await entityService.listAll({
              type: 'person',
              filters: { select: 'entity_data' },
            });
            if (!mounted || abortController.signal.aborted) return;
            const categories = [
              ...new Set(
                persons
                  .map((p) => (p.entity_data as Record<string, unknown> | null)?.billet)
                  .filter((b): b is string => typeof b === 'string' && b.trim().length > 0),
              ),
            ].sort((a, b) => a.localeCompare(b));
            setSelectOptions((prev) => ({
              ...prev,
              [param.name]: categories.map((c) => ({ value: c, label: c })),
            }));
          } catch (error) {
            if (!mounted || abortController.signal.aborted) return;
            notifications.error(
              'Failed to load Workforce Category options',
              getErrorMessage(error),
            );
            setSelectOptions((prev) => ({ ...prev, [param.name]: [] }));
          } finally {
            if (mounted && !abortController.signal.aborted) {
              setSelectLoading((prev) => ({ ...prev, [param.name]: false }));
            }
          }
        }

        if (param.type === 'select' && param.dynamicOptions === DYNAMIC_OPTIONS.LOCATIONS) {
          setSelectLoading((prev) => ({ ...prev, [param.name]: true }));
          try {
            const locations = await httpClient.get<
              Array<{ id: string; display_name: string; breadcrumb: string | null }>
            >(
              '/api/rest/data/v1/location_hierarchy?order=breadcrumb.asc&select=id,display_name,breadcrumb',
            );
            if (!mounted || abortController.signal.aborted) return;
            // Only leaf locations can hold assets; parent nodes would always return zero results.
            const leafLocations = filterLeafLocationOptions(
              locations.map((l) => ({
                id: l.id,
                breadcrumb: l.breadcrumb,
                display_name: l.display_name,
              })),
            );
            const options: SelectOptionItem[] = [
              { value: '', label: 'All Locations' },
              ...leafLocations.map((l) => ({ value: l.id, label: l.breadcrumb || l.display_name })),
            ];
            setSelectOptions((prev) => ({ ...prev, [param.name]: options }));
          } catch (error) {
            if (!mounted || abortController.signal.aborted) return;
            notifications.error('Failed to load locations', getErrorMessage(error));
            setSelectOptions((prev) => ({
              ...prev,
              [param.name]: [{ value: '', label: 'All Locations' }],
            }));
          } finally {
            if (mounted && !abortController.signal.aborted) {
              setSelectLoading((prev) => ({ ...prev, [param.name]: false }));
            }
          }
        }

        if (param.type === 'multiselect' && param.dynamicOptions === DYNAMIC_OPTIONS.IE_KITS) {
          setMultiselectLoading((prev) => ({ ...prev, [param.name]: true }));
          setMultiselectErrors((prev) => ({ ...prev, [param.name]: null }));

          try {
            // Fetch all IE kits using keyset pagination (AB#29164)
            const allIeKits = await entityService.listAll({
              type: 'ie_kit',
              order: 'display_name.asc',
              filters: { select: 'display_name,entity_data' },
            });

            if (!mounted || abortController.signal.aborted) return;

            // Deduplicate and filter out null/undefined names.
            // Fall back to entity_data.name if display_name is missing or
            // contains an unresolved template literal (e.g. '{{display_name}}').
            // Migration 138 (WI #28854) backfills display_name for existing entities;
            // this guard handles any residual rows not yet corrected by that migration.
            const resolveKitName = (kit: (typeof allIeKits)[number]): string | null => {
              const dn = kit.display_name;
              if (dn && dn !== '{{display_name}}') return dn;
              const name = (kit.entity_data as Record<string, unknown> | null)?.name;
              return typeof name === 'string' && name ? name : null;
            };
            const uniqueOptions = [
              ...new Set(
                allIeKits.map(resolveKitName).filter((name): name is string => Boolean(name)),
              ),
            ];

            setMultiselectOptions((prev) => ({ ...prev, [param.name]: uniqueOptions }));
          } catch (error) {
            if (!mounted || abortController.signal.aborted) return;

            const errorMessage = getErrorMessage(error);
            console.error(`Failed to load ${ieKitLabel.toLowerCase()} options:`, error);
            notifications.error(`Failed to load ${ieKitLabel.toLowerCase()} options`, errorMessage);

            setMultiselectOptions((prev) => ({ ...prev, [param.name]: [] }));
            setMultiselectErrors((prev) => ({ ...prev, [param.name]: errorMessage }));
          } finally {
            if (mounted && !abortController.signal.aborted) {
              setMultiselectLoading((prev) => ({ ...prev, [param.name]: false }));
            }
          }
        }
      }
    };

    loadDynamicOptions();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [selectedReport, notifications]);

  // Load shelf-map-locations multiselect options when the layoutId form value changes (#370).
  // This effect is separate so it can re-run each time the user picks a different layout.
  const selectedLayoutId = formValues['layoutId'] as string | undefined;
  useEffect(() => {
    if (!selectedReport) return;
    const locationParam = selectedReport.parameters.find(
      (p) =>
        p.type === 'multiselect' &&
        'dynamicOptions' in p &&
        p.dynamicOptions === DYNAMIC_OPTIONS.SHELF_MAP_LOCATIONS,
    );
    if (!locationParam) return;
    if (!selectedLayoutId) {
      locationNameToIdRef.current[locationParam.name] = {};
      setMultiselectOptions((prev) => ({ ...prev, [locationParam.name]: [] }));
      setFormValues((prev) => ({ ...prev, [locationParam.name]: [] }));
      return;
    }

    let mounted = true;
    setMultiselectLoading((prev) => ({ ...prev, [locationParam.name]: true }));
    setMultiselectErrors((prev) => ({ ...prev, [locationParam.name]: null }));

    shelfMapService
      .getLayout(selectedLayoutId)
      .then((layout) => {
        if (!mounted) return;
        // Collect unique, named locations from all slots (#532).
        // Build a map from location entity ID → { name, aisle, shelf } to detect
        // duplicate display names before constructing the options list.
        const idToMeta = new Map<string, { name: string; aisle: string; shelf: number }>();
        for (const slot of layout.slots ?? []) {
          const id = slot.location_entity_id;
          const name = slot.location_name;
          if (id && name && !idToMeta.has(id)) {
            idToMeta.set(id, {
              name,
              aisle: slot.aisle_label ?? '',
              shelf: slot.shelf_number ?? 0,
            });
          }
        }

        // Count how many distinct IDs share each display name.
        const nameCount = new Map<string, number>();
        for (const meta of idToMeta.values()) {
          nameCount.set(meta.name, (nameCount.get(meta.name) ?? 0) + 1);
        }

        // For duplicate names, disambiguate using the aisle+shelf path.
        const options: string[] = [];
        const nameToId: Record<string, string> = {};
        for (const [id, meta] of idToMeta.entries()) {
          const isDuplicate = (nameCount.get(meta.name) ?? 1) > 1;
          const label = isDuplicate ? `${meta.name} [${meta.aisle}-${meta.shelf}]` : meta.name;
          options.push(label);
          nameToId[label] = id;
        }
        locationNameToIdRef.current[locationParam.name] = nameToId;
        setMultiselectOptions((prev) => ({ ...prev, [locationParam.name]: options }));
        // Reset the locationIds selection when the layout changes
        setFormValues((prev) => ({ ...prev, [locationParam.name]: [] }));
      })
      .catch((error: unknown) => {
        if (!mounted) return;
        const errorMessage = getErrorMessage(error);
        setMultiselectOptions((prev) => ({ ...prev, [locationParam.name]: [] }));
        setMultiselectErrors((prev) => ({ ...prev, [locationParam.name]: errorMessage }));
      })
      .finally(() => {
        if (mounted) {
          setMultiselectLoading((prev) => ({ ...prev, [locationParam.name]: false }));
        }
      });

    return () => {
      mounted = false;
    };
  }, [selectedReport, selectedLayoutId]);

  // Load Section + Parent NSN dynamic options scoped to the selected Workforce Category, and
  // reload Parent NSN options when Section changes (total-clothing-size-by-workforce-category).
  const selectedWorkforceCategory = formValues['workforceCategory'] as string | undefined;
  const selectedSection = formValues['section'] as string | undefined;
  useEffect(() => {
    if (!selectedReport) return;
    const sectionParam = selectedReport.parameters.find(
      (p) =>
        p.type === 'select' &&
        'dynamicOptions' in p &&
        p.dynamicOptions === DYNAMIC_OPTIONS.SECTIONS_BY_WORKFORCE_CATEGORY,
    );
    const parentNsnParam = selectedReport.parameters.find(
      (p) =>
        p.type === 'multiselect' &&
        'dynamicOptions' in p &&
        p.dynamicOptions === DYNAMIC_OPTIONS.PARENT_NSN_BY_WORKFORCE_CATEGORY,
    );
    if (!sectionParam && !parentNsnParam) return;

    const workforceCategoryChanged =
      previousWorkforceCategoryRef.current !== selectedWorkforceCategory;
    previousWorkforceCategoryRef.current = selectedWorkforceCategory;

    if (!selectedWorkforceCategory) {
      if (sectionParam) {
        setSelectOptions((prev) => ({ ...prev, [sectionParam.name]: [] }));
        setFormValues((prev) => ({ ...prev, [sectionParam.name]: '' }));
      }
      if (parentNsnParam) {
        parentNsnLabelToIdRef.current = {};
        setMultiselectOptions((prev) => ({ ...prev, [parentNsnParam.name]: [] }));
        setFormValues((prev) => ({ ...prev, [parentNsnParam.name]: [] }));
      }
      return;
    }

    let mounted = true;

    const loadScopedOptions = async () => {
      if (sectionParam) {
        setSelectLoading((prev) => ({ ...prev, [sectionParam.name]: true }));
        try {
          const persons = await entityService.listAll({
            type: 'person',
            filters: {
              'entity_data->>billet': `eq.${selectedWorkforceCategory}`,
              select: 'entity_data',
            },
          });
          if (!mounted) return;
          const sections = [
            ...new Set(
              persons
                .map((p) => (p.entity_data as Record<string, unknown> | null)?.unit)
                .filter((u): u is string => typeof u === 'string' && u.trim().length > 0),
            ),
          ].sort((a, b) => a.localeCompare(b));
          setSelectOptions((prev) => ({
            ...prev,
            [sectionParam.name]: sections.map((s) => ({ value: s, label: s })),
          }));
        } catch (error) {
          if (!mounted) return;
          notifications.error('Failed to load Section options', getErrorMessage(error));
          setSelectOptions((prev) => ({ ...prev, [sectionParam.name]: [] }));
        } finally {
          if (mounted) setSelectLoading((prev) => ({ ...prev, [sectionParam.name]: false }));
        }
        // Reset the Section selection only when the upstream Workforce Category changed.
        if (workforceCategoryChanged) {
          setFormValues((prev) => ({ ...prev, [sectionParam.name]: '' }));
        }
      }

      if (parentNsnParam) {
        setMultiselectLoading((prev) => ({ ...prev, [parentNsnParam.name]: true }));
        setMultiselectErrors((prev) => ({ ...prev, [parentNsnParam.name]: null }));
        try {
          const personFilters: Record<string, string> = {
            'entity_data->>billet': `eq.${selectedWorkforceCategory}`,
            select: 'id',
          };
          if (selectedSection) {
            personFilters['entity_data->>unit'] = `eq.${selectedSection}`;
          }
          const persons = await entityService.listAll({ type: 'person', filters: personFilters });
          const personIds = persons.map((p) => p.id);

          let parentIds: string[] = [];
          if (personIds.length > 0) {
            const idFilter =
              personIds.length === 1 ? `eq.${personIds[0]}` : `in.(${personIds.join(',')})`;
            const sizingRows = await entityService.listAll({
              type: 'person_sizing',
              filters: { 'entity_data->>person_id': idFilter, select: 'entity_data' },
            });
            parentIds = [
              ...new Set(
                sizingRows
                  .map((row) => (row.entity_data as Record<string, unknown> | null)?.parent_id)
                  .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
              ),
            ];
          }

          if (!mounted) return;

          const options: string[] = [];
          const labelToId: Record<string, string> = {};
          if (parentIds.length > 0) {
            const parentIdFilter =
              parentIds.length === 1 ? `eq.${parentIds[0]}` : `in.(${parentIds.join(',')})`;
            const parentCatalogs = await entityService.listAll({
              type: 'catalog',
              filters: { id: parentIdFilter },
            });
            if (!mounted) return;
            for (const catalog of parentCatalogs) {
              const entityData = (catalog.entity_data as Record<string, unknown> | null) ?? {};
              const nsn = typeof entityData.nsn === 'string' ? entityData.nsn.trim() : '';
              const name = catalog.display_name ?? 'Unknown Item';
              const label = nsn ? `${nsn} — ${name}` : name;
              options.push(label);
              labelToId[label] = catalog.id;
            }
          }

          parentNsnLabelToIdRef.current = labelToId;
          setMultiselectOptions((prev) => ({ ...prev, [parentNsnParam.name]: options }));
        } catch (error) {
          if (!mounted) return;
          const errorMessage = getErrorMessage(error);
          setMultiselectOptions((prev) => ({ ...prev, [parentNsnParam.name]: [] }));
          setMultiselectErrors((prev) => ({ ...prev, [parentNsnParam.name]: errorMessage }));
        } finally {
          if (mounted) {
            setMultiselectLoading((prev) => ({ ...prev, [parentNsnParam.name]: false }));
          }
        }
        // Reset the Parent NSN selection whenever its option set is reloaded (Workforce
        // Category or Section changed).
        setFormValues((prev) => ({ ...prev, [parentNsnParam.name]: [] }));
      }
    };

    loadScopedOptions();

    return () => {
      mounted = false;
    };
  }, [selectedReport, selectedWorkforceCategory, selectedSection, notifications]);

  const handleChangeText = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeBoolean = (name: string, value: boolean) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeMultiSelect = (name: string, values: string[]) => {
    setFormValues((prev) => ({ ...prev, [name]: values }));
  };

  const [isRunning, setIsRunning] = useState(false);

  const selectedReportEnabled = Boolean(
    selectedReport && ENABLED_REPORT_IDS.has(selectedReport.id),
  );

  const handleRun = useCallback(async () => {
    if (!selectedReport || isRunning) {
      return;
    }

    const missingRequired = selectedReport.parameters.filter((param) => {
      if (!param.required) return false;
      if (param.type === 'boolean') return false;

      const raw =
        param.type === 'person' || param.type === 'multiperson'
          ? formValues[param.name]
          : (formValues[param.name] ?? param.defaultValue);
      if (param.type === 'multiselect') {
        return !Array.isArray(raw) || raw.length === 0;
      }
      if (param.type === 'person') {
        return raw == null || !(raw as PersonOption).id;
      }
      if (param.type === 'multiperson') {
        return !Array.isArray(raw) || (raw as PersonOption[]).length === 0;
      }
      if (raw == null) return true;
      if (typeof raw === 'string' && raw.trim().length === 0) return true;
      return false;
    });

    if (missingRequired.length > 0) {
      const labels = missingRequired.map((param) => param.label).join(', ');
      notifications.warning('Missing required parameters', `Please fill: ${labels}`);
      return;
    }

    setIsRunning(true);
    // Pre-open a blank tab synchronously during the user-gesture context — before
    // any await — so the browser doesn't block it as an unauthorised popup.
    // Excel uses a download anchor, not a new tab, so it doesn't need this.
    let preOpenedWindow: Window | null = null;
    if (outputFormat !== 'excel') {
      preOpenedWindow = preOpenWindow();
    }
    try {
      // For the warehouse-shelf-map report, translate selected location names
      // to location entity IDs using the name→id map built when the layout was loaded (#370).
      const resolvedFormValues = { ...formValues };
      if (selectedReport.id === 'warehouse-shelf-map') {
        const locationParam = selectedReport.parameters.find((p) => p.name === 'locationIds');
        if (locationParam) {
          const selectedNames = (formValues[locationParam.name] ?? []) as string[];
          const nameToId = locationNameToIdRef.current[locationParam.name] ?? {};
          resolvedFormValues[locationParam.name] = selectedNames
            .map((name) => nameToId[name])
            .filter(Boolean);
        }
      }

      // For total-clothing-size-by-workforce-category, translate selected Parent NSN
      // labels to catalog entity IDs using the label→id map built when options were loaded.
      if (selectedReport.id === 'total-clothing-size-by-workforce-category') {
        const parentNsnParam = selectedReport.parameters.find((p) => p.name === 'parentNsnIds');
        if (parentNsnParam) {
          const selectedLabels = (formValues[parentNsnParam.name] ?? []) as string[];
          const labelToId = parentNsnLabelToIdRef.current;
          resolvedFormValues[parentNsnParam.name] = selectedLabels
            .map((label) => labelToId[label])
            .filter(Boolean);
        }
      }

      await selectedReport.run({
        ...resolvedFormValues,
        [OUTPUT_FORMAT_KEY]: outputFormat,
        [PRE_OPENED_WINDOW_KEY]: preOpenedWindow,
        personLabels: person,
        ieKitLabel,
        catalogLocationLabels,
        // Enforce SOC-only filter for restricted roles (Finance, Comptroller, HR).
        // The excludeSoc/excludePnp params are hidden in the UI for these roles,
        // so we set socOnly here to ensure the report backend filters accordingly.
        ...(isRestrictedRole &&
          selectedReportId === 'assets-currently-assigned' && {
            socOnly: true,
            excludeSoc: false,
            excludePnp: false,
          }),
      });
    } catch (error) {
      if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
      const message = getErrorMessage(error) || 'Failed to generate report. Please try again.';
      notifications.error('Report generation failed', message);
    } finally {
      setIsRunning(false);
    }
  }, [
    selectedReport,
    selectedReportId,
    formValues,
    outputFormat,
    person,
    ieKitLabel,
    catalogLocationLabels,
    notifications,
    isRunning,
    isRestrictedRole,
  ]);

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate system reports. Choose a format below — HTML opens in a new tab, Excel downloads a
        spreadsheet, and PDF opens a print dialog.
      </Typography>

      <Card variant="outlined" sx={{ maxWidth: 1400, mx: 'auto', minHeight: '70vh' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Available Reports
              </Typography>
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search reports"
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Box>
              <List
                dense
                sx={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {filteredReports.map((report) => {
                  const isEnabled = ENABLED_REPORT_IDS.has(report.id);
                  return (
                    <ListItemButton
                      key={report.id}
                      ref={report.id === selectedReportId ? selectedItemRef : null}
                      selected={report.id === selectedReportId}
                      onClick={() => handleSelectReport(report)}
                    >
                      <ListItemText
                        primary={report.name}
                        secondary={report.description}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: isEnabled ? 'text.primary' : 'text.disabled',
                        }}
                        secondaryTypographyProps={{
                          color: isEnabled ? 'text.secondary' : 'text.disabled',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            <Box sx={{ flex: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Parameters
              </Typography>
              {selectedReport ? (
                <>
                  {isComptroller && selectedReportId === 'assets-currently-assigned' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {`This will only show ${programLabel} Assets.`}
                    </Alert>
                  )}

                  {selectedReport.parameters.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      This report does not require any parameters.
                    </Typography>
                  )}

                  {selectedReport.parameters.map((param) => {
                    const value = formValues[param.name];

                    // Hide excluded params for comptroller and HR
                    if (
                      isRestrictedRole &&
                      (isHr ? HR_HIDDEN_PARAMS : COMPTROLLER_HIDDEN_PARAMS).has(param.name)
                    ) {
                      return null;
                    }

                    if (param.type === 'boolean') {
                      return (
                        <Box key={param.name} sx={{ mb: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(value ?? param.defaultValue)}
                                onChange={(event) =>
                                  handleChangeBoolean(param.name, event.target.checked)
                                }
                                disabled={!selectedReportEnabled}
                              />
                            }
                            label={param.label}
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'select') {
                      const isDynamic = Boolean(param.dynamicOptions);
                      const isLoadingOpts = isDynamic && (selectLoading[param.name] ?? false);
                      const optsToRender: SelectOptionItem[] = isDynamic
                        ? (selectOptions[param.name] ?? [])
                        : (param.options ?? []).map((o) =>
                            typeof o === 'string' ? { value: o, label: toTitleCase(o) } : o,
                          );

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            select
                            label={param.label}
                            value={(value as string) ?? param.defaultValue ?? ''}
                            onChange={(event) => handleChangeText(param.name, event.target.value)}
                            disabled={!selectedReportEnabled || isLoadingOpts}
                          >
                            {!param.required && (
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                            )}
                            {isLoadingOpts ? (
                              <MenuItem disabled>Loading…</MenuItem>
                            ) : (
                              optsToRender.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))
                            )}
                          </TextField>
                        </Box>
                      );
                    }

                    if (param.type === 'multiselect') {
                      const selectedValues = Array.isArray(value)
                        ? (value as string[])
                        : (param.defaultValue ?? []);

                      // Use dynamic options if available, otherwise use static options
                      const options = param.dynamicOptions
                        ? (multiselectOptions[param.name] ?? [])
                        : (param.options ?? []);

                      const loading = param.dynamicOptions
                        ? (multiselectLoading[param.name] ?? false)
                        : false;

                      const error = param.dynamicOptions
                        ? (multiselectErrors[param.name] ?? null)
                        : null;

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            multiple
                            options={options}
                            value={selectedValues}
                            onChange={(_, newValue) =>
                              handleChangeMultiSelect(param.name, newValue)
                            }
                            disableCloseOnSelect
                            disabled={!selectedReportEnabled}
                            loading={loading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                error={!!error}
                                helperText={error}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? (
                                        <CircularProgress color="inherit" size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'person') {
                      const selectedPerson = value as PersonOption | null;
                      const options = personOptions[param.name] ?? [];
                      const loading = personLoading[param.name] ?? false;
                      const inputValue = personSearchInputs[param.name] ?? '';

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            options={options}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) =>
                              `${option.displayName}${option.entityIdentifier ? ` (${option.entityIdentifier})` : ''}`
                            }
                            value={selectedPerson}
                            onChange={(_, newValue) => {
                              setFormValues((prev) => ({ ...prev, [param.name]: newValue }));
                            }}
                            inputValue={inputValue}
                            onInputChange={(_, newInputValue) =>
                              handlePersonSearch(param.name, newInputValue)
                            }
                            loading={loading}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            disabled={!selectedReportEnabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                placeholder="Start typing to search..."
                                slotProps={{
                                  input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loading ? (
                                          <CircularProgress color="inherit" size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  },
                                }}
                              />
                            )}
                            noOptionsText={
                              inputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : `No ${person.lowerPlural} found`
                            }
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'multiperson') {
                      const selectedPersons = (value as PersonOption[]) ?? EMPTY_PERSONS;
                      const options = personOptions[param.name] ?? [];
                      const loading = personLoading[param.name] ?? false;
                      const inputValue = personSearchInputs[param.name] ?? '';

                      // Comptroller: all persons are pre-loaded — use client-side filtering
                      if (isComptroller) {
                        return (
                          <Box key={param.name} sx={{ mb: 2 }}>
                            <Autocomplete
                              multiple
                              options={options}
                              getOptionLabel={(option) =>
                                `${option.displayName}${option.entityIdentifier ? ` (${option.entityIdentifier})` : ''}`
                              }
                              value={selectedPersons}
                              onChange={(_, newValue) => {
                                setFormValues((prev) => ({ ...prev, [param.name]: newValue }));
                              }}
                              loading={loading}
                              isOptionEqualToValue={(option, val) => option.id === val.id}
                              disabled={!selectedReportEnabled}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={param.label}
                                  placeholder={`Search ${person.lowerPlural}...`}
                                  slotProps={{
                                    input: {
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {loading ? (
                                            <CircularProgress color="inherit" size={20} />
                                          ) : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    },
                                  }}
                                />
                              )}
                              noOptionsText={
                                loading ? 'Loading...' : `No ${person.lowerPlural} found`
                              }
                            />
                          </Box>
                        );
                      }

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            multiple
                            filterOptions={(x) => x}
                            options={options}
                            getOptionLabel={(option) =>
                              `${option.displayName}${option.entityIdentifier ? ` (${option.entityIdentifier})` : ''}`
                            }
                            value={selectedPersons}
                            onChange={(_, newValue) => {
                              setFormValues((prev) => ({ ...prev, [param.name]: newValue }));
                            }}
                            inputValue={inputValue}
                            onInputChange={(_, newInputValue) =>
                              handlePersonSearch(param.name, newInputValue)
                            }
                            loading={loading}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            disabled={!selectedReportEnabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                placeholder="Start typing to search..."
                                slotProps={{
                                  input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loading ? (
                                          <CircularProgress color="inherit" size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  },
                                }}
                              />
                            )}
                            noOptionsText={
                              inputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : `No ${person.lowerPlural} found`
                            }
                          />
                        </Box>
                      );
                    }

                    return (
                      <Box key={param.name} sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label={param.label}
                          value={(value as string) ?? ''}
                          onChange={(event) => handleChangeText(param.name, event.target.value)}
                          type={param.type === 'date' ? 'date' : 'text'}
                          InputLabelProps={param.type === 'date' ? { shrink: true } : undefined}
                          disabled={!selectedReportEnabled}
                        />
                      </Box>
                    );
                  })}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a report from the list to configure parameters and run it.
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          {selectedReport && !selectedReportEnabled && (
            <Typography variant="body2" color="error">
              Currently unavailable
            </Typography>
          )}
          <ToggleButtonGroup
            value={outputFormat}
            exclusive
            onChange={(_, value: ReportOutputFormat | null) => {
              if (value) setOutputFormat(value);
            }}
            size="small"
          >
            <ToggleButton value="html">HTML</ToggleButton>
            <ToggleButton value="excel">Excel</ToggleButton>
            <ToggleButton value="pdf">PDF</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={!selectedReport || !selectedReportEnabled || isRunning}
            startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isRunning ? 'Generating…' : 'Run Report'}
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};
